import { useMemo, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import UptimeDashboardKpiTile from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardKpiTile"
import UptimeOverviewRibbon, {
  buildRibbonCells,
  incidentCountToCellVariant,
  uptimePercentToCellVariant,
} from "@/v2-backoffice-client/uptime/UptimeOverviewRibbon"
import UptimeOverviewSparkline from "@/v2-backoffice-client/uptime/UptimeOverviewSparkline"
import UptimeStatusBreakdown from "@/v2-backoffice-client/uptime/UptimeStatusBreakdown"
import {
  aggregateDailyDriftDetections,
  UPTIME_SPARK_DAYS,
} from "@/v2-backoffice-client/uptime/uptime.charts.utils"
import {
  formatRelativeTime,
  formatUptimePercent,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import {
  formatDriftSeveritySummary,
  sumDriftSeverity,
  uptimeTone,
} from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.utils"
import type { UptimeOverviewFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.overview.fragment"
import type { UptimeInlineFragment } from "@/v2-backoffice-client/uptime/inline/__types__/uptime.inline.fragment"
import type { DashboardDriftByKey } from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.types"

export type UptimeDashboardKpisProps = ComponentProps<"div"> & {
  overview: UptimeOverviewFragment | null
  // Per-target list. Used purely for the chart aggregations behind
  // each KPI tile (status breakdown bar, daily uptime/incident
  // ribbons, drift detection bars). Optional -- the tiles still
  // render their headline numbers without it.
  targets?: UptimeInlineFragment[]
  driftCount: number
  driftByKey: DashboardDriftByKey
}

// Top KPI strip on the wall-display dashboard. Mirrors the regular
// page's `UptimeOverview` structure (one tile per metric, optional
// chart behind the text) but at the bigger always-on scale. Each
// tile gets the chart that best matches its metric:
//
//   - Targets             -> stacked status breakdown bar of the
//                            active fleet (up / down / slow /
//                            drifting / skipped). Muted targets are
//                            excluded -- they're shown as a small
//                            "+N muted" annotation in the sub-line.
//   - Uptime · 24 h       -> 30-day daily uptime ribbon (matches
//                            the per-target HistoryBar vocabulary)
//   - Active incidents    -> 30-day daily incident-count ribbon
//                            (red intensity ramp on the days
//                            something broke)
//   - Configuration drift -> violet bar sparkline of daily drift
//                            detections so a wave of new drift
//                            spikes visibly under the headline
function UptimeDashboardKpis(props: UptimeDashboardKpisProps) {
  const { overview, targets, driftCount, driftByKey, className, ...divProps } =
    props

  const targetsList = useMemo(() => targets ?? [], [targets])

  const dailyUptime = useMemo(
    () => dashboardDailyUptimeSeries(overview?.FleetDailyHistory),
    [overview],
  )
  const dailyIncidents = useMemo(
    () => dashboardDailyIncidentSeries(overview?.FleetDailyHistory),
    [overview],
  )
  const dailyDrifts = useMemo(
    () => aggregateDailyDriftDetections(targetsList),
    [targetsList],
  )

  const uptimeRibbon = useMemo(
    () =>
      buildRibbonCells({
        series: dailyUptime,
        days: UPTIME_SPARK_DAYS,
        toVariant: uptimePercentToCellVariant,
        formatTooltip: (v, date) =>
          v == null
            ? `${date}: no data`
            : `${date}: ${formatUptimePercent(v)} up`,
      }),
    [dailyUptime],
  )
  const incidentRibbon = useMemo(
    () =>
      buildRibbonCells({
        series: dailyIncidents,
        days: UPTIME_SPARK_DAYS,
        toVariant: incidentCountToCellVariant,
        formatTooltip: (v, date) => {
          const n = v ?? 0
          if (n === 0) return `${date}: no incidents`
          return `${date}: ${n} ${n === 1 ? "incident" : "incidents"}`
        },
      }),
    [dailyIncidents],
  )

  const driftSeverityTotals = useMemo(
    () => sumDriftSeverity(driftByKey),
    [driftByKey],
  )

  return (
    <div className={cn("uptime-dashboard-kpis", className)} {...divProps}>
      <UptimeDashboardKpiTile
        label="Targets"
        value={overview ? `${overview.UpCount}/${overview.TotalTargets}` : "—"}
        sub={
          overview
            ? `${overview.DownCount} down · ${overview.SkippedCount} skipped${
                overview.MutedCount > 0
                  ? ` · +${overview.MutedCount} muted`
                  : ""
              }`
            : ""
        }
        tone={
          overview && overview.DownCount > 0
            ? "critical"
            : overview && overview.SkippedCount > 0
              ? "warning"
              : "ok"
        }
        chart={
          // Status mix at the bottom of the tile: a thin segmented
          // bar of up / down / slow / drifting. Positioned via the
          // `uptime-dashboard-kpi-breakdown` modifier so the tile
          // text doesn't crowd it. The breakdown drops muted rows
          // internally so the segments line up with the headline.
          <UptimeStatusBreakdown
            targets={targetsList}
            className="uptime-dashboard-kpi-breakdown"
          />
        }
      />

      <UptimeDashboardKpiTile
        label="Uptime · 24 h"
        value={overview ? formatUptimePercent(overview.UptimePercent24h) : "—"}
        sub={
          overview
            ? `7 d ${formatUptimePercent(overview.UptimePercent7d)} · 30 d ${formatUptimePercent(overview.UptimePercent30d)}`
            : ""
        }
        tone={uptimeTone(overview?.UptimePercent24h ?? null)}
        chart={<UptimeOverviewRibbon cells={uptimeRibbon} />}
      />

      <UptimeDashboardKpiTile
        label="Active incidents"
        value={overview ? String(overview.ActiveIncidentCount) : "—"}
        sub={
          overview
            ? `${overview.IncidentsStarted24h} new · 24 h${
                overview.LastIncidentAt
                  ? ` · last ${formatRelativeTime(overview.LastIncidentAt)}`
                  : ""
              }`
            : ""
        }
        tone={overview && overview.ActiveIncidentCount > 0 ? "critical" : "ok"}
        chart={<UptimeOverviewRibbon cells={incidentRibbon} />}
      />

      <UptimeDashboardKpiTile
        label="Configuration drift"
        value={String(driftCount)}
        sub={
          driftCount > 0
            ? formatDriftSeveritySummary(driftSeverityTotals) ||
              "upstream metadata diverged"
            : "all baselines match"
        }
        tone={driftCount > 0 ? "drift" : "ok"}
        chart={
          <UptimeOverviewSparkline
            series={dailyDrifts}
            variant="violet"
            shape="bar"
          />
        }
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Fleet-level chart helpers (mirrors the ones in UptimeOverview.tsx but
// scoped to this module so there is no cross-component import).
// ---------------------------------------------------------------------------

type FleetDailyEntry = { Date: string; TotalChecks: number; DownChecks: number }

function dashboardDailyUptimeSeries(
  history: FleetDailyEntry[] | null | undefined,
): Array<number | null> {
  const map = new Map<string, FleetDailyEntry>()
  for (const d of history ?? []) map.set(d.Date, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    const d = map.get(key)
    out.push(
      d && d.TotalChecks > 0
        ? (100 * (d.TotalChecks - d.DownChecks)) / d.TotalChecks
        : null,
    )
  }
  return out
}

function dashboardDailyIncidentSeries(
  history: FleetDailyEntry[] | null | undefined,
): Array<number | null> {
  const map = new Map<string, FleetDailyEntry>()
  for (const d of history ?? []) map.set(d.Date, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    const d = map.get(key)
    out.push(d && d.DownChecks > 0 ? d.DownChecks : 0)
  }
  return out
}

export default UptimeDashboardKpis
